import React, { useEffect, useRef, useState } from 'react';
import './Navbar.css';
import { Navbar, Nav, Container, NavDropdown, Image } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaUser, FaShoppingCart, FaHeart, FaBox, FaEnvelope, FaCog, FaSignOutAlt, FaCaretDown } from 'react-icons/fa';
import SearchBar from '../common/SearchBar';
import logoImage from '../../assets/logo.png';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useWishlist } from '../../contexts/WishlistContext';

const BuyerNavbar = () => {
  const { currentUser, logout } = useAuth();
  const { getItemCount } = useCart();
  const { wishlistItems } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Handle navigation with proper same-page detection
  const handleNavigation = (path, event) => {
    // Prevent navigation if already on the same page
    if (location.pathname === path) {
      console.log('Already on page, just refreshing:', path);
      event?.preventDefault();
      // Close mobile menu if it's open
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
      return;
    }
    
    console.log('Navigating to:', path);
    navigate(path);
    
    // Close mobile menu after navigation
    setTimeout(() => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    }, 100);
  };

  const handleSearch = (query) => {
    if (query.trim()) {
      navigate(`/products?search=${encodeURIComponent(query)}`);
    }
  };

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        toggleRef.current &&
        !toggleRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const displayName = (
    `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || currentUser?.email || 'User'
  );

  const avatarUrl = currentUser?.profile_picture || currentUser?.profilePicture;

  return (
    <div className="sticky-top">
    <Navbar expand="lg" className="mb-4 navbar-shadow navbar-gradient buyer-navbar pe-0">
      <Container fluid className="position-relative pe-0">
        {/* UniTrade Logo/Brand */}
        <Navbar.Brand as={Link} to="/" className="navbar-brand-container">
          <img
            src={logoImage}
            alt="UniTrade Logo" 
            height="25" 
            className="me-2" 
          />
          <span className="brand-text">UniTrade</span>
        </Navbar.Brand>
        
        {/* Mobile Menu Toggle with User and Cart Icons */}
        <div className="d-flex d-lg-none align-items-center">
          {/* Cart Icon - Only show when logged in */}
          <Link to="/cart" className="nav-link me-2">
            <FaShoppingCart className="text-white" style={{ fontSize: '1.25rem' }} />
            {getItemCount() > 0 && (
              <span className="badge bg-warning rounded-pill ms-1" style={{ fontSize: '0.7rem', padding: '2px 4px' }}>
                {getItemCount()}
              </span>
            )}
          </Link>
          {/* User Icon - Only show when logged in */}
          <Link to="/profile" className="nav-link">
            <FaUser className="text-white" style={{ fontSize: '1.25rem' }} />
          </Link>
          {/* Mobile Menu Toggle */}
          <button 
            className="btn btn-link nav-link p-0 ms-1" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ cursor: 'pointer', textDecoration: 'none' }}
            aria-label="Toggle menu"
            ref={toggleRef}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <div className="d-flex flex-column justify-content-around" style={{ width: '24px', height: '24px' }}>
              <span className="d-block bg-white rounded" style={{ height: '2px', width: '100%' }}></span>
              <span className="d-block bg-white rounded" style={{ height: '2px', width: '100%' }}></span>
              <span className="d-block bg-white rounded" style={{ height: '2px', width: '100%' }}></span>
            </div>
          </button>
        </div>

        <Navbar.Toggle aria-controls="basic-navbar-nav" className="d-none" />
        
        <Navbar.Collapse id="basic-navbar-nav" className={mobileMenuOpen ? "show" : ""} ref={menuRef} >
          {/* Search Bar - Desktop */}
          <div className="d-none d-lg-flex justify-content-end flex-grow-1 ms-lg-3">
            <div style={{ maxWidth: '650px', width: '100%' }}>
              <SearchBar 
                placeholder="Search products..."
                className="w-100"
                value={searchQuery}
                onChange={setSearchQuery}
                onSubmit={handleSearch}
              />
            </div>
          </div>
          
          {/* User Menu */}
          <Nav className="ms-auto align-items-center pe-0">
            {/* Cart Icon - Desktop Only */}
            <Nav.Link as={Link} to="/cart" className="me-3 nav-icon-link d-none d-lg-flex align-items-center">
              <FaShoppingCart className="nav-icon" />
              {getItemCount() > 0 && (
                <span className="badge bg-warning rounded-pill ms-1 nav-badge">
                  {getItemCount()}
                </span>
              )}
            </Nav.Link>
            
            {/* Wishlist Icon - Desktop Only */}
            <Nav.Link as={Link} to="/wishlist" className="me-3 nav-icon-link d-none d-lg-flex align-items-center">
              <FaHeart className="nav-icon" />
              {wishlistItems.length > 0 && (
                <span className="badge bg-warning rounded-pill ms-1 nav-badge">
                  {wishlistItems.length}
                </span>
              )}
            </Nav.Link>
            
            {/* User Profile Dropdown - Desktop Only */}
            <div className="d-none d-lg-block align-self-center me-0">
              <NavDropdown 
                title={
                  <div className="nav-user-link-compact">
                    <FaUser className="nav-user-icon-compact" style={{ color: '#ffcc00', fontSize: '1rem' }} />
                    <span className="nav-user-name-compact">
                      {currentUser?.first_name || currentUser?.email?.split('@')[0] || 'User'}
                    </span>
                    <FaCaretDown className="nav-dropdown-arrow-compact" />
                  </div>
                } 
                id="buyer-nav-dropdown"
                align="end"
              >
                  <NavDropdown.Item as={Link} to="/profile">
                    <FaUser className="me-2" /> Profile
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/cart">
                    <FaShoppingCart className="me-2" /> Cart
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/wishlist">
                    <FaHeart className="me-2" /> Wishlist
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/orders">
                    <FaBox className="me-2" /> Orders
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/messages">
                    <FaEnvelope className="me-2" /> Messages
                  </NavDropdown.Item>
                  <NavDropdown.Item as={Link} to="/settings">
                    <FaCog className="me-2" /> Settings
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleLogout}>
                    <FaSignOutAlt className="me-2" /> Logout
                  </NavDropdown.Item>
                </NavDropdown>
            </div>
            
            {/* Mobile Menu Items - Only visible when mobile menu is open */}
            <div className="d-lg-none w-100 mt-3">
              {mobileMenuOpen && (
                <div className="mobile-menu-items">
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/profile', e)} 
                    className={`py-2 ${location.pathname === '/profile' ? 'active disabled' : ''}`}
                  >
                    <FaUser className="me-2" /> Profile
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/cart', e)} 
                    className={`py-2 ${location.pathname === '/cart' ? 'active disabled' : ''}`}
                  >
                    <FaShoppingCart className="me-2" /> Cart
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/wishlist', e)} 
                    className={`py-2 ${location.pathname === '/wishlist' ? 'active disabled' : ''}`}
                  >
                    <FaHeart className="me-2" /> Wishlist
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/orders', e)} 
                    className={`py-2 ${location.pathname === '/orders' ? 'active disabled' : ''}`}
                  >
                    <FaBox className="me-2" /> Orders
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/messages', e)} 
                    className={`py-2 ${location.pathname === '/messages' ? 'active disabled' : ''}`}
                  >
                    <FaEnvelope className="me-2" /> Messages
                  </Nav.Link>
                  <Nav.Link 
                    as="button" 
                    onClick={(e) => handleNavigation('/settings', e)} 
                    className={`py-2 ${location.pathname === '/settings' ? 'active disabled' : ''}`}
                  >
                    <FaCog className="me-2" /> Settings
                  </Nav.Link>
                  <hr className="my-2" />
                  <Nav.Link onClick={handleLogout} className="py-2">
                    <FaSignOutAlt className="me-2" /> Logout
                  </Nav.Link>
                </div>
              )}
            </div>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
    </div>
  );
};

export default BuyerNavbar;
